export const DAY_CODES = ['SU', 'MO', 'TU', 'WE', 'TH', 'FR', 'SA'];

// Colors are used as text/border on the card background — these are the
// WCAG-AA (≥4.5:1) darkened variants of the original palette hues.
export const MOOD_META = {
  1: { emoji: '😢', label: 'Rough', color: '#9e5353' },
  2: { emoji: '😕', label: 'Off', color: '#87613a' },
  3: { emoji: '😐', label: 'Steady', color: '#72675c' },
  4: { emoji: '🙂', label: 'Good', color: '#487246' },
  5: { emoji: '😊', label: 'Great', color: '#4d7a4b' }
};

export function uuid() {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID();
  return 'id-' + Math.random().toString(36).slice(2) + Date.now().toString(36);
}

export function isoDate(d = new Date()) {
  return d.toISOString().slice(0, 10);
}

export function startOfDay(d = new Date()) {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x;
}

export function mondayOf(d = new Date()) {
  const x = startOfDay(d);
  const day = x.getDay();
  const offset = (day + 6) % 7;
  x.setDate(x.getDate() - offset);
  return x;
}

export function daysBetween(a, b) {
  return Math.round((startOfDay(b) - startOfDay(a)) / 86400000);
}

export function formatRelativeDay(dateStr) {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  const diff = daysBetween(d, new Date());
  if (diff === 0) return 'today';
  if (diff === 1) return 'yesterday';
  if (diff < 7) return `${diff}d ago`;
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

export function dueDateState(dueDate) {
  if (!dueDate) return null;
  const due = new Date(dueDate);
  const today = startOfDay(new Date());
  const diff = daysBetween(today, due);
  if (diff < 0) return { state: 'overdue', label: `${-diff}d overdue` };
  if (diff <= 7) return { state: 'soon', label: `due in ${diff}d` };
  return { state: 'future', label: due.toLocaleDateString(undefined, { month: 'short', day: 'numeric' }) };
}

function checkInDates(checkIns) {
  return new Set((checkIns || []).map((c) => (c.date || '').slice(0, 10)));
}

export function dailyStreak(checkIns) {
  const dates = checkInDates(checkIns);
  let streak = 0;
  const cursor = new Date();
  cursor.setHours(12, 0, 0, 0);
  if (!dates.has(isoDate(cursor))) {
    cursor.setDate(cursor.getDate() - 1);
  }
  while (dates.has(isoDate(cursor))) {
    streak++;
    cursor.setDate(cursor.getDate() - 1);
    if (streak > 9999) break;
  }
  return streak;
}

export function daysOfWeekStreak(checkIns, days) {
  const dates = checkInDates(checkIns);
  const allowed = new Set(days || []);
  if (!allowed.size) return 0;
  let streak = 0;
  const cursor = new Date();
  cursor.setHours(12, 0, 0, 0);
  cursor.setDate(cursor.getDate() - 1);
  for (let i = 0; i < 365; i++) {
    const code = DAY_CODES[cursor.getDay()];
    if (allowed.has(code)) {
      if (dates.has(isoDate(cursor))) streak++;
      else break;
    }
    cursor.setDate(cursor.getDate() - 1);
  }
  return streak;
}

export function weeklyStreak(checkIns, target) {
  if (!target || target < 1) return 0;
  const counts = new Map();
  for (const c of checkIns || []) {
    const w = mondayOf(new Date(c.date)).toISOString().slice(0, 10);
    counts.set(w, (counts.get(w) || 0) + 1);
  }
  let streak = 0;
  const thisMonday = mondayOf(new Date());
  const cursor = new Date(thisMonday);
  for (let i = 0; i < 520; i++) {
    const key = cursor.toISOString().slice(0, 10);
    const cnt = counts.get(key) || 0;
    if (cnt >= target) {
      streak++;
    } else if (cursor.getTime() === thisMonday.getTime()) {
      // current week in progress — doesn't break
    } else {
      break;
    }
    cursor.setDate(cursor.getDate() - 7);
  }
  return streak;
}

export function cleanStreak(checkIns) {
  if (!checkIns || !checkIns.length) return null;
  const sorted = checkIns
    .map((c) => (c.date || '').slice(0, 10))
    .filter(Boolean)
    .sort();
  const last = sorted[sorted.length - 1];
  if (!last) return null;
  const lastDate = new Date(last + 'T12:00:00Z');
  return Math.max(0, daysBetween(lastDate, new Date()));
}

export function thisWeekCount(checkIns) {
  const monday = mondayOf(new Date());
  return (checkIns || []).filter((c) => new Date(c.date) >= monday).length;
}

export function lastWeekCount(checkIns) {
  const thisMonday = mondayOf(new Date());
  const lastMonday = new Date(thisMonday);
  lastMonday.setDate(lastMonday.getDate() - 7);
  return (checkIns || []).filter((c) => {
    const d = new Date(c.date);
    return d >= lastMonday && d < thisMonday;
  }).length;
}

export function computeStreak(habit) {
  if (!habit || habit.paused) return habit?.streak ?? 0;
  if (habit.type === 'negative') return cleanStreak(habit.checkIns);
  if (habit.type === 'reduce') return null;

  const freq = habit.frequency || { type: 'daily' };
  if (freq.type === 'days') return daysOfWeekStreak(habit.checkIns, freq.days);
  if (freq.type === 'weekly') return weeklyStreak(habit.checkIns, freq.count || 3);
  return dailyStreak(habit.checkIns);
}

export function checkedInToday(habit) {
  if (!habit?.checkIns?.length) return false;
  const today = isoDate();
  return habit.checkIns.some((c) => (c.date || '').slice(0, 10) === today);
}
